/**
 * Las pantallas que entraron al shell nuevo POR EL DESKTOP —
 * ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas.
 *
 * Son las rutas que vivían en el grupo de `DashboardLayout` y por eso montaban el
 * `<aside>` viejo también en desktop. Se mudaron al grupo del shell nuevo para que la
 * sidebar sea la misma en todos lados, pero NO nacieron para él: se escribieron contra
 * el panel de `.main-content` (ancho completo, alto definido, su propio padding y sus
 * propios tamaños de título).
 *
 * Fueron 13 en la primera mudanza (ADR-2026-08-20) y se sumaron dos en la segunda
 * (ADR-2026-09-10-lo-que-enlaza-la-sidebar-nueva-vive-en-el-shell-nuevo), cuando se
 * volvió a cruzar el grupo viejo contra lo que enlaza el shell nuevo — el barrido
 * original no podía conocer una lista de ítems que se escribió después.
 *
 * Esta lista es lo que las distingue de las secciones nativas del shell, y la leen dos
 * lugares por dos motivos distintos:
 *
 *  - `EstiloMinimal` la usa para hospedarlas en NEUTRO (sin la columna del 90% ni el
 *    reset de `.shell-seccion`) —
 *    ADR-2026-08-20-el-shell-hospeda-en-neutro-lo-que-no-nacio-para-la-columna.
 *  - `ShellMobile` la usa para dejarles la barra inferior VIEJA, que es la que tienen
 *    hoy. La mudanza es un arreglo de desktop; mobile no se toca.
 *
 * Prefijos, no rutas exactas: `/estudio-marketing` cubre sus ocho pantallas. Ojo con
 * `/subscription/success`, que va entero a propósito: `/subscription` (el catálogo de
 * planes) SÍ es nativa del shell desde ADR-2026-08-04-subscription-entra-al-shell-nuevo,
 * y un prefijo `/subscription` se la llevaría puesta.
 */
export const RUTAS_SHELL_LEGACY = [
  // Los tres modos con pantalla propia (`Modos.tsx` intercepta su path y navega acá).
  // Son los que enlaza "Todas las herramientas" de la sidebar nueva, o sea el camino
  // por el que se reportó el salto de chrome.
  '/shorts-studio',
  '/animar-imagen',
  '/carrusel',
  // La pantalla intermedia de los atajos —
  // ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen. Está construida
  // con el mismo molde que los tres de arriba (`min-h-screen` + su propia columna
  // `max-w-content`), y en su paso 2 monta el visor del historial, que también trae lo
  // suyo. Con la columna del 90% del shell encima quedaría doblemente sangrada.
  '/atajo',
  // Pantalla dedicada del Agente. La abre el botón de expandir de `PanelChatAtlas`, el
  // ítem de la sidebar y —desde ADR-2026-09-14-el-agente-es-de-pro-y-ultra— la card del
  // sheet de Generar en mobile, que es la primera puerta que tiene en el teléfono.
  //
  // Estar en esta lista es lo que en mobile le deja la barra inferior VIEJA, que se
  // auto-limita a `MOBILE_BOTTOM_NAV_PATHS` y por lo tanto no se dibuja acá: el Agente se
  // queda con la pantalla entera y su composer no queda tapado. Sale gratis, pero es la
  // razón por la que `/agente` no necesita estar además en `MOBILE_RUTAS_SIN_NAV`.
  //
  // `/chat` es el alias viejo (redirige a `/agente`) y queda por si el redirect tarda
  // un paint.
  '/agente',
  '/chat',
  // El modo Diseño de indumentaria es una pantalla de trabajo a ancho completo, como el
  // Agente: se hospeda en neutro (ADR-2026-09-17-un-diseno-es-un-chat). Prefijo: cubre
  // `/diseno-de-indumentaria/<id>`.
  '/diseno-de-indumentaria',
  // El modo Redes, por el mismo motivo que Diseño: lienzo a pantalla completa
  // (ADR-2026-10-01-modo-redes). Prefijo: cubre `/redes/<sesionId>`. No pisa
  // `/redes-sociales/...`: el match exige `/redes` exacto o `/redes/`.
  '/redes',
  // El Catálogo con el agente (ADR-2026-10-13-catalogo-con-el-agente): pantalla de trabajo a
  // ancho completo, como Redes. Prefijo: cubre `/catalogo/<sesionId>`.
  '/catalogo',
  // Estudio de marketing: el prefijo cubre mapa, plan, plan/:piezaId, insights,
  // estrategia, marca, datos y onboarding.
  '/estudio-marketing',
  // Post-checkout.
  '/subscription/success',
  // Las otras dos que enlaza la sidebar nueva, mudadas por el mismo motivo que los
  // modos de arriba — ADR-2026-09-10-lo-que-enlaza-la-sidebar-nueva-vive-en-el-shell-nuevo.
  // Son ítems devOnly de `ACCESOS_INTERNOS`; entran en NEUTRO porque las dos se
  // escribieron contra el panel de `.main-content` del shell viejo.
  '/redes-sociales/calendario',
  '/test-v1',
  // El editor de Flujos (canvas de nodos, ADR-2026-09-04-canvas-flujos-enfoque). No es
  // legacy: entra acá porque un canvas infinito necesita el alto entero del `<main>`,
  // sin la columna del 90% ni el padding de `.shell-seccion`. Sólo el editor: la lista
  // `/flujos` es una sección normal del shell (y `/flujo` no la prefija).
  '/flujo',
] as const;

/** `true` si `pathname` cae en una de las rutas mudadas (o en una hija suya). */
export function esRutaShellLegacy(pathname: string): boolean {
  return RUTAS_SHELL_LEGACY.some(
    (ruta) => pathname === ruta || pathname.startsWith(`${ruta}/`),
  );
}
